import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, type LinkProps } from 'react-router-dom';
import type { AdminSession } from '../../shared/src/types/admin';

/**
 * Staff view as someone with `?impersonate=<user id>` in the address bar, as in Capital Company
 * and LanternPass: no cookie and no token. The server re-checks that the signed-in person is
 * staff on every request, so the parameter grants nothing on its own.
 */
export const IMPERSONATE_PARAM = 'impersonate';
const VIEWING_KEY = 'soulthread.viewing-as';

export function impersonatedUserId(search = window.location.search): string | null {
  const value = new URLSearchParams(search).get(IMPERSONATE_PARAM);
  if (!value || !/^[\w-]{1,64}$/.test(value)) return null;
  // Only a view started from /admin in this tab counts, so a link someone sends does nothing.
  return sessionStorage.getItem(VIEWING_KEY) === value ? value : null;
}

export const startViewingAs = (userId: string) => sessionStorage.setItem(VIEWING_KEY, userId);
export const stopViewingAs = () => sessionStorage.removeItem(VIEWING_KEY);

/** Keeps the person being viewed across in-app navigation. */
export function withImpersonation(to: string): string {
  const userId = impersonatedUserId();
  if (!userId || !to.startsWith('/app')) return to;
  const url = new URL(to, window.location.origin);
  url.searchParams.set(IMPERSONATE_PARAM, userId);
  return url.pathname + url.search + url.hash;
}

export function authHeaders(token: string): Record<string, string> {
  const userId = impersonatedUserId();
  return {
    Authorization: `Bearer ${token}`,
    ...(userId ? { 'X-SoulThread-Impersonate': userId } : {}),
  };
}

export function AppLink({ to, ...props }: Omit<LinkProps, 'to'> & { to: string }) {
  return <Link to={withImpersonation(to)} {...props} />;
}

export function useAppNavigate() {
  const navigate = useNavigate();
  return useCallback((to: string) => navigate(withImpersonation(to)), [navigate]);
}

/** Whether the signed-in person is staff, and whom they are viewing as. */
export function useAdminSession(token: string): AdminSession | null {
  const [session, setSession] = useState<AdminSession | null>(null);
  const viewing = impersonatedUserId();
  useEffect(() => {
    setSession(null);
    if (!token) return;
    const controller = new AbortController();
    fetch('/api/admin-console/session', { headers: authHeaders(token), signal: controller.signal })
      .then((response) => (response.ok ? (response.json() as Promise<AdminSession>) : null))
      .then((value) => {
        if (value) setSession(value);
      })
      .catch(() => undefined);
    return () => controller.abort();
  }, [token, viewing]);
  return session;
}

export function ImpersonationBanner({ phoneNumber }: { phoneNumber: string }) {
  return (
    <div className="impersonation-frame" role="status">
      <div className="impersonation-banner">
        <span>
          Viewing as <strong>{phoneNumber}</strong> · read-only
        </span>
        <Link to="/admin" onClick={stopViewingAs}>
          Stop
        </Link>
      </div>
    </div>
  );
}
